import React from 'react';
import { motion } from 'framer-motion';
import { Target, Heart, Users, Award, Globe, Leaf, TrendingUp, Zap, CheckCircle, Play, Linkedin, Mic, Mail, ExternalLink, FlaskConical, BookOpen, Microscope } from 'lucide-react';
import SEO from '../components/SEO';

export default function About() {
  const values = [
    {
      icon: Leaf,
      title: 'Science of Nature',
      description: 'We combine the healing power of nature with rigorous scientific validation.',
    },
    {
      icon: Award,
      title: 'Quality Excellence',
      description: 'GMP-certified manufacturing ensures the highest quality standards.',
    },
    {
      icon: Heart,
      title: 'Customer First',
      description: 'Your health and satisfaction are at the heart of everything we do.',
    },
    {
      icon: Globe,
      title: 'Global Impact',
      description: 'Making clinically-studied innovations accessible to communities worldwide.',
    },
  ];

  const objectives = [
    {
      period: 'Short Term (2025-2026)',
      title: 'Product Launch, Community Access & Awareness',
      description: 'Launch Rhuleave-K® and BCM-95® through Community Interest Companies (CICs) and charities across the UK, bypassing traditional retail to ensure affordable, trusted access. Starting with founder funding, we plan to secure further investment to grow. A comprehensive public awareness campaign will educate communities and healthcare professionals about mitochondrial health and the benefits of natural supplements, building trust and promoting informed health choices nationwide.',
      icon: Zap,
    },
    {
      period: 'Medium Term (2027-2028)',
      title: 'Expansion & Product Innovation',
      description: 'Initially fund clinical research internally, with plans to secure additional support to advance further studies. This approach ensures sustained development of our innovations while expanding partnerships and building a trusted, science-backed brand. Over time we aim to broaden our range with new natural formulations.',
      icon: TrendingUp,
    },
    {
      period: 'Long Term (2029 & Beyond)',
      title: 'Leadership & Global Impact',
      description: 'Become a leading brand in science-driven, socially responsible health supplements in the UK and globally. Expand our mitochondrial-focused product range targeting chronic and age-related diseases while scaling our community-based distribution model internationally. Create a circular system where profits support ongoing research and public health initiatives, linking innovation directly to lasting community impact.',
      icon: Globe,
    },
  ];

  const team = [
    {
      name: 'Rupinderjit Kaur Aulak',
      role: 'Director',
      description: 'Strategic business development and partnership management',
      photo: 'https://customer-assets.emergentagent.com/job_f260d794-36ba-40b5-a520-313307251818/artifacts/xr78q3a1_WhatsApp%20Image%202026-02-22%20at%2010.24.42%20PM.jpeg',
      email: 'ra@iiypv.com',
    },
    {
      name: 'Nilesh Sutar',
      role: 'Finance Officer',
      description: 'Handling financial operations and business growth strategy',
      photo: 'https://customer-assets.emergentagent.com/job_bcm-wellness/artifacts/vqu9mp40_NILESH%27S%20PHOTO%20%281%29.jpg',
      email: 'accounts@iiypv.com',
    },
    {
      name: 'Livlong Nutraceuticals Ltd',
      role: 'Manufacturer',
      description: 'A prominent Indian manufacturer specializing in advanced nutraceuticals, dietary supplements, and herbal extracts. A subsidiary of Arjuna Natural Pvt Ltd.',
      photo: 'https://customer-assets.emergentagent.com/job_bcm-wellness/artifacts/ljx0a8kg_Arjuna%20Natural.jpg',
      email: null,
    },
    {
      name: 'Charity Foundation Partner',
      role: 'Community Partner',
      description: 'Community distribution and health outreach partner',
      photo: null,
      email: null,
    },
  ];

  const differentiators = [
    'Clinically validated & patented formulations with documented evidence',
    'Natural, clean-label, non-pharmaceutical alternatives',
    'Strong alignment with preventive health and innovation trends',
    'Focused, specialized product offering with research backing',
    'Multiple distribution channels serving diverse customer segments',
    'Partnership with Community Interest Companies for accessible healthcare',
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title="About IIY Phytoventra | UK Curcumin Brand | Our Story"
        description="Meet IIY Phytoventra — a UK phytonutrient brand and the UK distributor of BCM-95® curcumin supplements made with Curcugreen®, bringing clinically validated formulations to UK households."
      />
      <section className="gradient-hero py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <span className="text-sm font-medium tracking-widest uppercase text-primary mb-4 block">
              Our Story
            </span>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 font-heading" data-testid="about-page-title">
              Invest In Yourself.
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed mb-3">
              <strong>IIY Phytoventra</strong> is a UK phytonutrient brand built around a single belief: your daily wellbeing is the most worthwhile investment you can ever make.
            </p>
            <p className="text-base text-muted-foreground/80 max-w-3xl mx-auto leading-relaxed italic">
              Unlocking the science of Nature for an Active Life by Investing In Yourself.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ============ Trademark Reveal Section ============ */}
      <section className="py-20 md:py-28 bg-gradient-to-b from-amber-50/40 via-white to-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{backgroundImage:'radial-gradient(circle at 25% 30%, #2d5016 0px, transparent 1px), radial-gradient(circle at 75% 70%, #b08d3a 0px, transparent 1px)', backgroundSize:'60px 60px'}} />
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 relative">
          <div className="grid md:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left: Trademark video */}
            <motion.div
              initial={{ opacity: 0, scale: 0.92 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.7 }}
              className="relative"
            >
              <div className="relative aspect-square rounded-3xl overflow-hidden shadow-2xl ring-1 ring-amber-700/20 bg-gradient-to-br from-amber-100/40 to-stone-50">
                <video
                  src="/iiy-trademark.mp4"
                  poster="/iiy-trademark.jpg"
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="metadata"
                  className="w-full h-full object-cover"
                  data-testid="iiy-trademark-video"
                />
                <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur-sm px-3 py-1.5 rounded-full shadow-md">
                  <span className="text-[10px] font-bold tracking-[0.2em] text-amber-800 uppercase">Registered Trademark</span>
                </div>
              </div>
              {/* Decorative ribbon */}
              <div className="absolute -top-3 -right-3 bg-primary text-white text-xs font-bold uppercase tracking-wider px-4 py-2 rounded-full shadow-lg">
                IIY &reg;
              </div>
            </motion.div>

            {/* Right: Brand narrative */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6, delay: 0.15 }}
            >
              <div className="inline-flex items-center gap-2 bg-amber-100/60 text-amber-900 px-4 py-1.5 rounded-full mb-5">
                <span className="text-[11px] font-bold uppercase tracking-[0.18em]">Brand Heritage</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold mb-5 font-heading leading-tight">
                A new name. The same scientific spine.
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-5">
                The <strong>IIY trademark</strong> brings together the four pillars of our philosophy &mdash; the meditative figure (you), the DNA double-helix (the science), the leaves emerging from above (Nature's healing power) and the sun rays (vitality). It is the visual promise we make to every customer.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-7">
                <strong>IIY Phytoventra</strong> is the UK home for patented, clinically validated phytonutrient formulations &mdash; led today by <strong>BCM-95®</strong>. Our mission is to put gold-standard scientific evidence directly into the hands of UK households who want to take charge of their own wellbeing &mdash; one daily, deliberate investment at a time.
              </p>

              <div className="bg-emerald-50 rounded-2xl border border-emerald-100 p-5 mb-7" data-testid="about-philosophy">
                <h3 className="text-lg font-bold text-emerald-900 mb-2 font-heading">The Philosophy Behind Invest In Yourself (IIY)</h3>
                <p className="text-muted-foreground leading-relaxed">
                  At IIY Phytoventra, our "Invest In Yourself" philosophy is founded on the belief that sustained vitality starts with cellular health and preventative, science-backed botanical nutrition. By pairing clinically studied natural extracts (like BCM-95®) with qualified medical herbalist expertise, we empower individuals to take active ownership of their longevity and mobility.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white rounded-xl p-4 border border-primary/10 shadow-sm">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground/80 mb-1">Public Brand</div>
                  <div className="text-lg font-bold text-primary">IIY Phytoventra</div>
                  <div className="text-xs text-muted-foreground">Invest In Yourself &reg;</div>
                </div>
                <div className="bg-white rounded-xl p-4 border border-primary/10 shadow-sm" data-testid="legal-entity-card">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground/80 mb-1">Legal Entity</div>
                  <div className="text-lg font-bold text-secondary">IIY Phytoventra Ltd</div>
                  <div className="text-xs text-muted-foreground">UK Reg No: 10646999</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-white" data-testid="foundation-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          {/* Foundation: tightened 2-col layout — narrative left, credibility stat-grid right */}
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-start">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-7"
            >
              <span className="text-[11px] font-semibold tracking-[0.25em] uppercase text-primary mb-3 block">Our Foundation</span>
              <h2 className="text-4xl md:text-5xl font-bold mb-6 font-heading leading-tight" data-testid="about-who-we-are">
                A UK consumer brand for patented, clinically validated phytonutrients.
              </h2>

              <div className="space-y-4 text-muted-foreground leading-relaxed">
                <p>
                  <strong>IIY Phytoventra Ltd</strong> is the UK consumer arm for a curated portfolio of patented, clinically validated phytonutrient formulations. We source our ingredients directly from the original inventors and manufacturers and bring them into the United Kingdom under one trusted commercial roof &mdash; with full batch traceability, UK quality control, and end-to-end consumer accountability.
                </p>
                <p>
                  Our flagship lines &mdash; <strong>BCM-95®</strong>, a highly bioavailable curcumin extract, and <strong>Rhuleave-K<sup>&reg;</sup></strong>, a patented natural supplement &mdash; have been the subject of gold-standard, Phase III clinical research across a range of study areas.
                </p>
                <p>
                  Operating from Northampton, England, with VAT-registered UK supply, sequential UK invoicing, and 14-day UK Consumer Contracts Regulations returns &mdash; we are built to serve UK households the way a serious supplement brand should.
                </p>
              </div>
            </motion.div>

            {/* Right: visual credibility / fact card */}
            <motion.aside
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-5 lg:sticky lg:top-28"
              data-testid="foundation-stats"
            >
              <div className="rounded-2xl bg-gradient-to-br from-primary/5 via-white to-amber-50/40 border border-primary/10 shadow-sm p-6 md:p-7">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white rounded-xl p-4 border border-primary/10 shadow-sm">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground/80 mb-1">Public Brand</div>
                    <div className="text-base md:text-lg font-bold text-primary leading-tight">IIY Phytoventra</div>
                    <div className="text-[11px] text-muted-foreground">Invest In Yourself &reg;</div>
                  </div>
                  <div className="bg-white rounded-xl p-4 border border-primary/10 shadow-sm" data-testid="legal-entity-card">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground/80 mb-1">Legal Entity</div>
                    <div className="text-base md:text-lg font-bold text-secondary leading-tight">IIY Phytoventra Ltd</div>
                    <div className="text-[11px] text-muted-foreground">UK Limited Company</div>
                  </div>
                  <div className="bg-white rounded-xl p-4 border border-primary/10 shadow-sm">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground/80 mb-1">UK Reg No</div>
                    <div className="text-base md:text-lg font-bold text-foreground leading-tight">10646999</div>
                    <div className="text-[11px] text-muted-foreground">Companies House, England &amp; Wales</div>
                  </div>
                  <div className="bg-white rounded-xl p-4 border border-primary/10 shadow-sm">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground/80 mb-1">VAT</div>
                    <div className="text-base md:text-lg font-bold text-foreground leading-tight">GB503280921</div>
                    <div className="text-[11px] text-muted-foreground">UK VAT Registered</div>
                  </div>
                </div>

                <div className="mt-5 pt-5 border-t border-primary/10 grid grid-cols-3 gap-3 text-center">
                  <div>
                    <div className="text-2xl md:text-3xl font-bold text-primary font-heading">75+</div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">Peer-reviewed studies</div>
                  </div>
                  <div>
                    <div className="text-2xl md:text-3xl font-bold text-primary font-heading">III</div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">Phase clinical trials</div>
                  </div>
                  <div>
                    <div className="text-2xl md:text-3xl font-bold text-primary font-heading">8h</div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">Active in body</div>
                  </div>
                </div>

                <div className="mt-5 pt-5 border-t border-primary/10 flex flex-wrap gap-2">
                  <span className="inline-flex items-center gap-1.5 bg-primary/8 text-primary text-[11px] font-semibold px-2.5 py-1 rounded-full"><CheckCircle className="w-3 h-3" /> GMP Certified</span>
                  <span className="inline-flex items-center gap-1.5 bg-primary/8 text-primary text-[11px] font-semibold px-2.5 py-1 rounded-full"><CheckCircle className="w-3 h-3" /> Vegan</span>
                  <span className="inline-flex items-center gap-1.5 bg-primary/8 text-primary text-[11px] font-semibold px-2.5 py-1 rounded-full"><CheckCircle className="w-3 h-3" /> Non-GMO</span>
                  <span className="inline-flex items-center gap-1.5 bg-primary/8 text-primary text-[11px] font-semibold px-2.5 py-1 rounded-full"><CheckCircle className="w-3 h-3" /> Patented</span>
                  <span className="inline-flex items-center gap-1.5 bg-primary/8 text-primary text-[11px] font-semibold px-2.5 py-1 rounded-full"><CheckCircle className="w-3 h-3" /> UK Distributed</span>
                </div>
              </div>
            </motion.aside>
          </div>

        </div>
      </section>

      <section className="py-24 md:py-32 gradient-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 font-heading">Our Vision</h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              Revolutionizing the health supplement market through cutting-edge, scientifically validated innovation solutions
            </p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white rounded-3xl p-10 md:p-12 shadow-soft max-w-5xl mx-auto"
          >
            <div className="space-y-6 text-muted-foreground leading-relaxed">
              <p className="text-lg">
                IIY's vision is to revolutionize the health supplement market by delivering cutting-edge, scientifically validated innovation solutions that improve quality of life—particularly for adults managing musculoskeletal pain and older adults concerned with cognitive health.
              </p>
              <p>
                We are committed to becoming a trusted name in the industry, known for natural, effective, and research-driven products that address urgent health challenges. We aim to empower communities by working in partnership with Community Interest Companies (CICs) and charitable organisations across the UK.
              </p>
              <p>
                By doing so, we ensure greater access to innovation solutions, while reinvesting profits into public health initiatives. Our goal is to distribute thousands of units of products like Rhuleave-K® and BCM-95®, while introducing novel developments such as a Curcuma-based oral nasal spray to naturally support immunity against COVID, influenza, and Alzheimer's—offering a vaccine-like preventive approach.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 font-heading">Strategic Objectives</h2>
          </div>

          <div className="space-y-8">
            {objectives.map((objective, index) => (
              <motion.div
                key={objective.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-white to-primary/5 rounded-3xl p-8 md:p-10 shadow-soft"
              >
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <objective.icon className="w-8 h-8 text-primary" />
                  </div>
                  <div className="flex-1">
                    <span className="text-sm font-semibold text-primary uppercase tracking-wider">{objective.period}</span>
                    <h3 className="text-2xl font-bold mb-3 font-heading mt-2">{objective.title}</h3>
                    <p className="text-muted-foreground leading-relaxed">{objective.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 gradient-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 font-heading">Why Choose IIY</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              What sets us apart in the natural health supplement industry
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {differentiators.map((item, index) => (
              <motion.div
                key={item}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl p-6 shadow-soft flex items-start gap-4"
              >
                <CheckCircle className="w-6 h-6 text-primary flex-shrink-0 mt-0.5" />
                <p className="text-muted-foreground leading-relaxed">{item}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 font-heading">Our Team</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Leadership and partners driving IIY's mission forward
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
            {team.map((member, index) => (
              <motion.div
                key={member.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-white to-primary/5 rounded-2xl p-6 text-center shadow-soft"
              >
                <div className="w-28 h-28 rounded-full overflow-hidden mx-auto mb-4 border-4 border-primary/20 flex items-center justify-center bg-primary/10">
                  {member.photo ? (
                    <img loading="lazy" decoding="async" 
                      src={member.photo} 
                      alt={member.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Users className="w-14 h-14 text-primary/60" />
                  )}
                </div>
                <h3 className="text-base font-bold mb-1 font-heading">{member.name}</h3>
                <p className="text-sm text-primary font-semibold mb-2">{member.role}</p>
                <p className="text-xs text-muted-foreground leading-relaxed mb-3">{member.description}</p>
                {member.email && (
                  <a 
                    href={`mailto:${member.email}`}
                    className="inline-flex items-center gap-1.5 text-xs text-secondary hover:text-primary transition-colors"
                  >
                    <Mail className="w-3.5 h-3.5" />
                    {member.email}
                  </a>
                )}
              </motion.div>
            ))}
          </div>

          {/* Sales Contact */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 text-center"
          >
            <div className="inline-flex flex-wrap items-center justify-center gap-3 bg-white rounded-full px-6 py-4 shadow-soft">
              <Mail className="w-5 h-5 text-primary" />
              <span className="text-muted-foreground">Sales:</span>
              <a
                href="mailto:sales@iiypv.com"
                className="font-semibold text-secondary hover:text-primary transition-colors"
                data-testid="about-sales-email"
              >
                sales@iiypv.com
              </a>
              <span className="text-muted-foreground">·</span>
              <span className="text-muted-foreground">Support:</span>
              <a
                href="mailto:support@iiypv.com"
                className="font-semibold text-secondary hover:text-primary transition-colors"
                data-testid="about-support-email"
              >
                support@iiypv.com
              </a>
            </div>
          </motion.div>

          {/* ========= Scientific Advisory Board ========= */}
          <motion.div
            id="scientific-advisor"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mt-24 scroll-mt-24"
            data-testid="scientific-advisory-board"
          >
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-5 py-2 rounded-full mb-4">
                <Microscope className="w-4 h-4" />
                <span className="text-sm font-semibold tracking-wider uppercase">Scientific Advisory Board</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold font-heading">
                Guided by World-Class Science
              </h2>
            </div>

            <div className="bg-gradient-to-br from-primary/5 via-white to-white rounded-3xl overflow-hidden shadow-xl border border-primary/10">
              <div className="grid md:grid-cols-5 gap-0">
                {/* Portrait */}
                <div className="md:col-span-2 relative overflow-hidden bg-gradient-to-br from-primary/10 to-primary/5 min-h-[340px] md:min-h-full">
                  <img loading="lazy" decoding="async"
                    src="/dr-benny-antony.png"
                    alt="Dr. Benny Antony, PhD - Scientific Advisor, IIY Phytoventra"
                    className="w-full h-full object-cover object-center"
                    data-testid="dr-benny-photo"
                  />
                  <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm px-3 py-1.5 rounded-full shadow-md">
                    <span className="text-xs font-semibold text-primary tracking-wide">Scientific Advisor</span>
                  </div>
                </div>

                {/* Bio */}
                <div className="md:col-span-3 p-8 md:p-12">
                  <h3 className="text-3xl md:text-4xl font-bold mb-1 font-heading" data-testid="dr-benny-name">
                    Dr. Benny Antony, PhD
                  </h3>
                  <p className="text-primary font-semibold mb-6 text-sm tracking-wide uppercase">
                    Scientific Advisor &middot; IIY Phytoventra
                  </p>

                  <p className="text-muted-foreground leading-relaxed mb-6">
                    Dr. Benny Antony is an internationally recognised scientist whose career in natural-product chemistry, phytopharmaceutical research, and clinical validation has shaped the modern curcumin category. At IIY, he provides high-level scientific oversight on product development, ensuring every formulation we bring to market is rooted in peer-reviewed evidence and GMP-certified manufacturing standards.
                  </p>

                  {/* Research Focus pillars */}
                  <div className="grid sm:grid-cols-3 gap-3 mb-7">
                    <div className="bg-white rounded-xl p-4 border border-primary/10">
                      <FlaskConical className="w-5 h-5 text-primary mb-2" />
                      <h4 className="font-semibold text-sm mb-1">Bioavailability Science</h4>
                      <p className="text-xs text-muted-foreground leading-relaxed">Delivery systems that dramatically improve absorption of herbal actives.</p>
                    </div>
                    <div className="bg-white rounded-xl p-4 border border-primary/10">
                      <Leaf className="w-5 h-5 text-primary mb-2" />
                      <h4 className="font-semibold text-sm mb-1">Curcuminoid Research</h4>
                      <p className="text-xs text-muted-foreground leading-relaxed">Pioneering work on BCM-95<sup>&reg;</sup> / Curcugreen<sup>&reg;</sup>, a high-potency turmeric extract.</p>
                    </div>
                    <div className="bg-white rounded-xl p-4 border border-primary/10">
                      <Heart className="w-5 h-5 text-primary mb-2" />
                      <h4 className="font-semibold text-sm mb-1">Inflammatory Pathways</h4>
                      <p className="text-xs text-muted-foreground leading-relaxed">Role of natural antioxidants in chronic inflammation and joint health.</p>
                    </div>
                  </div>

                  {/* Publications & Patents */}
                  <div className="border-l-4 border-primary/30 pl-5 mb-6">
                    <div className="flex items-center gap-2 mb-3">
                      <BookOpen className="w-4 h-4 text-primary" />
                      <h4 className="font-semibold text-sm uppercase tracking-wider text-primary">Published Research &amp; Patents</h4>
                    </div>
                    <ul className="space-y-3 text-sm text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                        <span>
                          <a
                            href="https://www.cochranelibrary.com/central/doi/10.1002/central/CN-00998820/full#abstract"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-secondary hover:text-primary underline decoration-dotted underline-offset-2 font-medium"
                            data-testid="dr-benny-cochrane-link"
                          >
                            Cochrane Central Register of Controlled Trials &mdash; BCM-95® randomised clinical trial
                          </a>
                          <span className="block text-xs mt-1 leading-relaxed">
                            Registry entry on the Cochrane Library cataloguing a controlled clinical trial of BCM-95® curcumin formulation &mdash; included in Cochrane's gold-standard evidence database used by clinicians worldwide.
                          </span>
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                        <span>
                          <a
                            href="https://doi.org/10.1016/j.jad.2014.06.001"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-secondary hover:text-primary underline decoration-dotted underline-offset-2 font-medium"
                            data-testid="dr-benny-jad-link"
                          >
                            Curcumin for the Treatment of Major Depression &mdash; <em>Journal of Affective Disorders</em> (2014)
                          </a>
                          <span className="block text-xs mt-1 leading-relaxed">
                            Randomised, double-blind, placebo-controlled study (Lopresti et al., 2014) demonstrating curcumin's efficacy as an adjunct in adults with major depressive disorder &mdash; landmark peer-reviewed evidence on curcumin's effects beyond joint health.
                          </span>
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                        <span>Inventor of multiple patented extraction technologies for turmeric, boswellia and other medicinal plants.</span>
                      </li>
                    </ul>
                    <a
                      href="https://scholar.google.com/scholar?q=Benny+Antony+curcumin"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs text-primary font-semibold mt-4 hover:underline"
                      data-testid="dr-benny-scholar-link"
                    >
                      View full bibliography on Google Scholar <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>

                  {/* Quote */}
                  <blockquote className="italic text-muted-foreground border-l-2 border-primary/40 pl-4 text-sm leading-relaxed">
                    "Dr. Antony's commitment to bridging traditional herbal wisdom with modern clinical science ensures IIY Phytoventra Ltd stays at the forefront of evidence-based joint health support."
                  </blockquote>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Medilink Midlands Membership Call-Out Box */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-20"
            data-testid="medilink-membership-section"
          >
            <div className="bg-gradient-to-br from-white to-orange-50 rounded-3xl p-8 md:p-12 shadow-soft border border-orange-100/50">
              <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12">
                <div className="flex-shrink-0">
                  <a
                    href="https://portal.medilinkmidlands.com/member-directory/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img loading="lazy" decoding="async"
                      src="/medilink-midlands-member.png"
                      alt="Medilink Midlands Member Logo - IIY"
                      className="h-40 md:h-48 w-auto hover:scale-105 transition-transform duration-300"
                    />
                  </a>
                </div>
                <div className="flex-1 text-center md:text-left">
                  <h3 className="text-2xl md:text-3xl font-bold mb-4 font-heading text-secondary">
                    Proudly Connected
                  </h3>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    We are proud to be an <strong>Active Member of <a href="https://www.medilinkmidlands.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-bold">Medilink Midlands</a></strong>, the UK's leading life sciences and MedTech industry association. This membership positions IIY at the forefront of healthcare innovation, connecting us with the best clinical, academic, and commercial expertise across the Midlands and beyond.
                  </p>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    Through our <strong className="text-secondary">Medilink Midlands membership</strong>, we actively participate in shaping the future of the life sciences sector — collaborating with pioneering organisations, accessing strategic industry insights, and strengthening the partnerships that drive meaningful health outcomes for communities across the United Kingdom.
                  </p>
                  <a
                    href="https://portal.medilinkmidlands.com/member-directory/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white rounded-full px-6 py-3 font-semibold shadow-md hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5"
                    data-testid="medilink-view-network-btn"
                  >
                    View Our Network
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Media Section - Photos, Audio & Video */}
      <section className="py-24 md:py-32 gradient-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 font-heading">Media</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Photos, audio interviews, and videos featuring IIY
            </p>
          </div>

          {/* Audio Section */}
          <div className="max-w-4xl mx-auto mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-3xl p-8 md:p-10 shadow-soft"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center">
                  <Mic className="w-7 h-7 text-primary" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold font-heading">BBC Radio Humberside Interview</h3>
                  <p className="text-sm text-muted-foreground">Featured Radio Interview</p>
                </div>
              </div>
              
              <div className="mb-6">
                <audio 
                  controls 
                  className="w-full rounded-lg"
                  preload="metadata"
                >
                  <source 
                    src="https://customer-assets.emergentagent.com/job_f260d794-36ba-40b5-a520-313307251818/artifacts/3xc6it4q_14%20-%20BBC%20Radio%20Humberside%20-%20IIY%20%282%29.mp3" 
                    type="audio/mpeg" 
                  />
                  Your browser does not support the audio element.
                </audio>
              </div>

              <div className="bg-primary/5 rounded-2xl p-6">
                <h4 className="font-semibold mb-3 text-secondary">Interview Summary</h4>
                <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                  In this BBC Radio Humberside interview, the IIY Phytoventra team discusses the challenges of arthritis management and introduces our innovative approach to natural health supplements. The interview highlights how our specialised turmeric extract overcomes the poor bioavailability of traditional curcumin, keeping active compounds in the bloodstream for 8&ndash;10 hours.
                </p>
                <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                  Key topics covered include the bioavailability advantages of patented turmeric extracts, the importance of plant-derived turmerones in enhancing absorption naturally, and the depth of published research supporting Curcugreen<sup>&reg;</sup> (BCM-95®) as a clean-label alternative to standard turmeric. The interview also touches on holistic lifestyle factors &mdash; balanced nutrition, mindful daily routines, and active living &mdash; that complement a quality food supplement.
                </p>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  The interview emphasises IIY Phytoventra&apos;s commitment to addressing chronic inflammation through science-backed, natural solutions, making science-backed health supplements accessible to communities across the UK.
                </p>
              </div>
            </motion.div>
          </div>

          {/* Malta MedTech Event Section */}
          <div className="max-w-5xl mx-auto mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-3xl p-8 md:p-10 shadow-soft"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center">
                  <Globe className="w-7 h-7 text-primary" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold font-heading">UK–Malta MedTech & Life Sciences Collaboration Event</h3>
                  <p className="text-sm text-muted-foreground">Malta • February 18, 2026</p>
                </div>
              </div>

              {/* Event Photos */}
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <div className="rounded-2xl overflow-hidden">
                  <img loading="lazy" decoding="async" 
                    src="https://customer-assets.emergentagent.com/job_f260d794-36ba-40b5-a520-313307251818/artifacts/57es2vhd_WhatsApp%20Image%202026-02-18%20at%2011.39.24%20AM.jpeg"
                    alt="IIY Phytoventra at the UK\u2013Malta MedTech Event"
                    className="w-full h-64 object-cover"
                  />
                </div>
                <div className="rounded-2xl overflow-hidden">
                  <img loading="lazy" decoding="async" 
                    src="https://customer-assets.emergentagent.com/job_f260d794-36ba-40b5-a520-313307251818/artifacts/4aoiu3q5_WhatsApp%20Image%202026-02-22%20at%2011.03.04%20PM.jpeg"
                    alt="UK Malta MedTech and Life Sciences Event Banner"
                    className="w-full h-64 object-cover"
                  />
                </div>
              </div>

              <div className="bg-primary/5 rounded-2xl p-6">
                <h4 className="font-semibold mb-3 text-secondary">IIY at the UK–Malta MedTech & Life Sciences Collaboration Event</h4>
                
                <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                  On 18 February 2026, IIY Phytoventra&apos;s leadership represented the brand at a prestigious MedTech and Life Sciences collaborative discussion held in Malta. The event was part of a significant initiative supported by the British High Commission Malta, alongside Malta Enterprise and MedTech Makers Lab, bringing together leading innovators, entrepreneurs, and experts from the UK and Malta to explore strengthening cooperation in healthcare innovation and life sciences.
                </p>

                <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                  The focus of the discussions was on streamlining innovation, deepening mutual understanding of the rapidly evolving life sciences ecosystems in both countries, and identifying opportunities for strategic partnerships that can accelerate translational research, clinical development, and market expansion in MedTech, biotechnology, and health innovation sectors.
                </p>

                <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                  <strong className="text-secondary">Key Discussion Themes:</strong>
                </p>
                <ul className="text-muted-foreground text-sm leading-relaxed mb-4 list-disc list-inside space-y-1 ml-2">
                  <li>Enhancing cross-border innovation collaboration in MedTech and biotech</li>
                  <li>Identifying shared expertise in clinical research and regulatory pathways</li>
                  <li>Supporting early-stage companies to expand internationally</li>
                  <li>Fostering partnerships that can enhance patient outcomes while stimulating growth in both economies</li>
                </ul>

                <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                  The event underscored the growing importance of international cooperation in life sciences — especially between the UK, a global leader in health technologies and clinical research, and Malta, a fast-growing hub for innovation and entrepreneurial activity in the Mediterranean. The programme featured productive discussions, networking sessions, and knowledge sharing that highlighted how collaboration can unlock greater economic and scientific impact across the sector.
                </p>

                <p className="text-muted-foreground text-sm leading-relaxed">
                  This participation enabled IIY Phytoventra to connect with pioneering organisations and thought leaders in the MedTech and life sciences landscape, reinforcing our commitment to innovation and strategic collaboration beyond the UK. This engagement aligns with IIY Phytoventra&apos;s mission to leverage cutting-edge science and international partnerships to drive positive change in joint health support and active living.
                </p>
              </div>
            </motion.div>
          </div>

          {/* Connect Section with LinkedIn */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h3 className="text-2xl font-bold mb-4 font-heading">Connect With Us</h3>
            <p className="text-muted-foreground mb-6">Follow us on LinkedIn for the latest updates and news</p>
            <a
              href="https://www.linkedin.com/company/110393231"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-[#0077B5] hover:bg-[#006396] text-white rounded-full px-8 py-4 font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <Linkedin className="w-6 h-6" />
              Follow IIY on LinkedIn
            </a>
          </motion.div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-secondary text-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6 font-heading">
              Join Us on the Journey to Better Health
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Experience the difference that clinically-studied, scientifically-backed supplements can make in your life
            </p>
            <button
              onClick={() => window.location.href = '/products'}
              className="bg-primary hover:bg-primary/90 text-white rounded-full px-10 py-5 text-lg font-semibold shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
              data-testid="about-cta-button"
            >
              Explore Our Products
            </button>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
